/**
 * The environment the wrapper app hands the page — WebBridge.md §1 and the two
 * pushes of §2 that keep it current.
 *
 * ❓ PLACEHOLDER — nothing here is in the decomp. The shipped game is a Unity
 * app and reads `Screen.safeArea` / `Application.systemLanguage` in-process; a
 * WebView build has neither, so the wrapper injects them as globals before the
 * module runs. Contract from `reference/Rule&Template/WebBridge.md` §1.
 *
 * Five values, and they are of two different kinds:
 *
 *   `statusBarHeight` / `statusBarHeightPx` / `language`  — fixed for the session
 *   `isOnline` / `appActive`                              — a *snapshot at open*
 *
 * The second kind is the trap. Both are documented as "tại lúc mở" (at open
 * time) and go stale the moment the player pulls down the notification shade or
 * loses signal; the app then pushes `app_state_changed` / `network_changed`.
 * Reading `window.appActive` per frame would therefore report "foregrounded"
 * for a phone that has been in a pocket for an hour — which is exactly the bug
 * rule_LOGLEVEL §4d says makes a level report an eight-hour duration. So this
 * file reads the global **once**, at construction, and from then on the pushes
 * own the value.
 *
 * `document.hidden` is folded into `isActive` alongside the native flag rather
 * than replacing it: a plain browser tab has no `app_state_changed` at all, and
 * a WebView host may fire the native event without ever setting `hidden`.
 * Either one going false is enough to stop the clock.
 */

import { nativeBridge, type NativeBridge } from './bridge'

/** WebBridge.md §4 — the mapped set. Anything else the app sends means `en`. */
export const SUPPORTED_LANGUAGES = [
  'en', 'vi', 'pt-BR', 'pt', 'de', 'fr', 'zh-Hans', 'zh-Hant', 'hi', 'th', 'id',
  'ko', 'ja', 'ru', 'es', 'es-MX', 'es-AR', 'fil', 'it', 'tr', 'ar', 'nl', 'pl',
] as const

export type HostLanguage = (typeof SUPPORTED_LANGUAGES)[number]

/** The globals the wrapper sets on `window` before the bundle loads. */
export interface HostGlobals {
  statusBarHeight?: unknown
  statusBarHeightPx?: unknown
  language?: unknown
  isOnline?: unknown
  appActive?: unknown
}

/** Only the slice of `document` this needs, so a test can pass a stub. */
export interface VisibilitySource {
  hidden: boolean
  addEventListener(type: 'visibilitychange', listener: () => void): void
}

/**
 * A status bar is a small number of dp. A wrapper that has not measured yet
 * sends nothing; one that is confused sends a screen height. Both would push
 * the whole HUD off the top, so the value is clamped rather than trusted —
 * 96 dp is comfortably above any shipping notch and far below a screen.
 */
export const MAX_STATUS_BAR_DP = 96

function readNumber(value: unknown, max: number): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.min(n, max)
}

/** WebBridge.md §4 — "không khớp → `en`". */
export function normaliseLanguage(value: unknown): HostLanguage {
  if (typeof value !== 'string') return 'en'
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value) ? (value as HostLanguage) : 'en'
}

export class NativeHostEnvironment {
  /** dp — WebBridge.md §1 says this one is already usable as CSS px. */
  readonly statusBarHeight: number
  /** Raw device px, for anything that has to work in device space. */
  readonly statusBarHeightPx: number
  readonly language: HostLanguage

  private online: boolean
  /** The native half of "is the player looking at this?". */
  private nativeActive: boolean

  private readonly activeListeners: ((active: boolean) => void)[] = []

  constructor(
    globals: HostGlobals,
    private readonly visibility: VisibilitySource | null = null,
    bridge: NativeBridge = nativeBridge,
  ) {
    this.statusBarHeight = readNumber(globals.statusBarHeight, MAX_STATUS_BAR_DP)
    // The px value is a *device* measurement, so it may legitimately be several
    // times the dp one on a dense screen. Cap it against the dp ceiling scaled
    // by the largest DPR worth believing rather than against the dp ceiling.
    this.statusBarHeightPx = readNumber(globals.statusBarHeightPx, MAX_STATUS_BAR_DP * 4)
    this.language = normaliseLanguage(globals.language)
    // Absent means "no wrapper", i.e. a plain browser — which is online and
    // foregrounded until something says otherwise. `=== false` rather than
    // `!== true` so an undefined global does not read as a backgrounded app.
    this.online = globals.isOnline !== false
    this.nativeActive = globals.appActive !== false

    bridge.onNetworkChanged((online) => {
      this.online = online
    })
    bridge.onAppStateChanged((active) => {
      this.nativeActive = active
      this.emitActive()
    })
    visibility?.addEventListener('visibilitychange', () => this.emitActive())
  }

  /** WebBridge.md §1 `isOnline`, kept current by `network_changed`. */
  get isOnline(): boolean {
    return this.online
  }

  /**
   * True only while the player can actually see the board: the app is in the
   * foreground **and** the document is visible.
   */
  get isActive(): boolean {
    return this.nativeActive && !(this.visibility?.hidden ?? false)
  }

  onActiveChanged(listener: (active: boolean) => void): void {
    this.activeListeners.push(listener)
  }

  private emitActive(): void {
    const active = this.isActive
    for (const listener of this.activeListeners) listener(active)
  }
}

function browserGlobals(): HostGlobals {
  // `?statusBarHeight=44` — there is no notch in a desktop browser, so this is
  // the only way to see the safe-area inset without a device.
  const forced = new URLSearchParams(location.search).get('statusBarHeight')
  const injected = window as unknown as HostGlobals
  if (forced === null || forced === '') return injected
  return { ...injected, statusBarHeight: Number(forced), statusBarHeightPx: Number(forced) }
}

/**
 * The environment this build actually runs in.
 *
 * `document` is guarded because this module is imported by `npm test`, which
 * runs the bundle under Node: a missing document is "no visibility signal", not
 * a hidden page, so `isActive` then rests on the native flag alone.
 */
export const nativeHost = new NativeHostEnvironment(
  browserGlobals(),
  typeof document === 'undefined' ? null : document,
)
